import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Download, Eye, Plus } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import {
  agentApi,
  agentInstitutionPortfolioApi as api,
  agentAgencyDocumentsApi,
} from "@/services/agentApi";
import {
  PortfolioList,
  PortfolioDetail,
  PortfolioInstitution,
} from "../../../../shared/src/components/institution-portfolio/institutionPortfolioApi";
import {
  PortfolioOverlay,
  PortfolioFilePreview,
} from "../../../../shared/src/components/institution-portfolio/PortfolioOverlay";
import {
  AgencyDocumentsWorkspace,
  DocumentPagination,
  documentError,
} from "../../../../shared/src/components/agency-documents/AgencyDocumentsWorkspace";
import "../../../../shared/src/components/institution-portfolio/institutionPortfolio.css";
const date = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString("en-GB", {
        timeZone: "Africa/Lagos",
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "Not specified";
const label = (value: string) =>
  value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, " ");
function Summary({ data }: { data: PortfolioList["summary"] }) {
  return (
    <div className="portfolio-summary">
      {[
        ["Available", data.available],
        ["In progress", data.inProgress],
        ["Activated", data.activated],
      ].map(([key, value]) => (
        <div key={key}>
          <span>{key}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}
export function PartnershipsPage() {
  const { user } = useAuth();
  return (
    <PortfolioSession
      key={String(user?.id || "signed-out")}
      accountKey={String(user?.id || "signed-out")}
    />
  );
}
function PortfolioSession({ accountKey }: { accountKey: string }) {
  const [params, setParams] = useSearchParams();
  const initialId = Number(params.get("institutionId"));
  const [data, setData] = useState<PortfolioList | null>(null),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true),
    [searchInput, setSearchInput] = useState(""),
    [search, setSearch] = useState(""),
    [state, setState] = useState(""),
    [page, setPage] = useState(1),
    [refresh, setRefresh] = useState(0),
    [selected, setSelected] = useState(
      Number.isSafeInteger(initialId) && initialId > 0 ? initialId : 0,
    ),
    [adding, setAdding] = useState(false),
    [detail, setDetail] = useState<PortfolioDetail | null>(null),
    [detailError, setDetailError] = useState(""),
    [detailLoading, setDetailLoading] = useState(false),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState("");
  const [preview, setPreview] = useState<{
    url: string;
    name: string;
    mimeType: string;
  } | null>(null);
  const alive = useRef(true),
    previewRef = useRef<string | null>(null),
    fileSequence = useRef(0),
    detailInstitution = useRef(0);
  useEffect(() => {
    const raw = Number(params.get("institutionId"));
    setSelected(Number.isSafeInteger(raw) && raw > 0 ? raw : 0);
  }, [params]);
  useEffect(() => {
    const update = () => {
      if (document.visibilityState !== "hidden") setRefresh((n) => n + 1);
    };
    window.addEventListener("focus", update);
    const timer = window.setInterval(update, 60000);
    return () => {
      window.removeEventListener("focus", update);
      window.clearInterval(timer);
    };
  }, []);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      fileSequence.current++;
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    };
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    void api
      .list(
        { page, ...(search ? { search } : {}), ...(state ? { state } : {}) },
        controller.signal,
      )
      .then((result) => {
        if (!controller.signal.aborted) setData(result);
      })
      .catch((e) => {
        if (!controller.signal.aborted) {
          setError(documentError(e));
          if ([401, 403].includes(e?.response?.status)) setData(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [search, state, page, refresh]);
  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    const controller = new AbortController();
    if (detailInstitution.current !== selected) setDetail(null);
    detailInstitution.current = selected;
    setDetailError("");
    setDetailLoading(true);
    void api
      .detail(selected, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setDetail(result);
      })
      .catch((e) => {
        if (!controller.signal.aborted) {
          setDetailError(documentError(e));
          if ([401, 403, 404].includes(e?.response?.status)) {
            setDetail(null);
            fileSequence.current++;
            if (previewRef.current) URL.revokeObjectURL(previewRef.current);
            previewRef.current = null;
            setPreview(null);
          }
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetailLoading(false);
      });
    return () => controller.abort();
  }, [selected, refresh]);
  const close = () => {
    setSelected(0);
    setDetail(null);
    const next = new URLSearchParams(params);
    next.delete("institutionId");
    setParams(next, { replace: true });
  };
  const select = (id: number) => {
    setSelected(id);
    const next = new URLSearchParams(params);
    next.set("institutionId", String(id));
    setParams(next, { replace: true });
  };
  const request = async () => {
    if (!selected || busy) return;
    setBusy(true);
    setDetailError("");
    try {
      await agentApi.requestPartnership(selected);
      if (alive.current) {
        setNotice(
          "Partnership requested. Approval and a confirmed signed agreement are required for activation.",
        );
        setRefresh((n) => n + 1);
        setAdding(false);
      }
    } catch (e) {
      if (alive.current) setDetailError(documentError(e));
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  const file = async (download: boolean) => {
    if (!detail?.applicableAgreement || busy) return;
    const current = ++fileSequence.current;
    const agreement = detail.applicableAgreement;
    setBusy(true);
    try {
      const blob = await api.template(selected, download);
      if (!alive.current || current !== fileSequence.current) return;
      const url = URL.createObjectURL(blob);
      if (download) {
        const a = document.createElement("a");
        a.href = url;
        a.download = agreement.fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else {
        if (previewRef.current) URL.revokeObjectURL(previewRef.current);
        previewRef.current = url;
        setPreview({
          url,
          name: agreement.fileName,
          mimeType: agreement.mimeType || blob.type,
        });
      }
    } catch (e) {
      if (alive.current) setDetailError(documentError(e));
    } finally {
      if (alive.current && current === fileSequence.current) setBusy(false);
    }
  };
  const closePreview = () => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    previewRef.current = null;
    setPreview(null);
  };
  const row = (institution: PortfolioInstitution) => (
    <article className="portfolio-row" key={institution.id}>
      <div>
        <div className="portfolio-row__name">{institution.name}</div>
        <div className="portfolio-row__location">
          {[
            institution.city,
            institution.country || institution.countries?.name,
          ]
            .filter(Boolean)
            .join(", ") || "Location not provided"}
        </div>
      </div>
      <div>
        <span
          className={`portfolio-pill ${institution.partnershipStatus === "APPROVED" ? "portfolio-pill--active" : "portfolio-pill--pending"}`}
        >
          {institution.partnershipStatus
            ? label(institution.partnershipStatus)
            : "Not requested"}
        </span>
      </div>
      <div>
        <span
          className={`portfolio-pill ${!institution.eligibility.actionRequired && institution.eligibility.canSubmit ? "portfolio-pill--active" : institution.state === "AVAILABLE" ? "" : "portfolio-pill--pending"}`}
        >
          {institution.eligibility.actionRequired
            ? "Action required"
            : institution.eligibility.canSubmit
              ? "Activated"
              : institution.state === "AVAILABLE"
                ? "Available"
                : "Action required"}
        </span>
        {(institution.eligibility.actionRequired ||
          !institution.eligibility.canSubmit) && (
          <p className="portfolio-reason">
            {institution.eligibility.actionReason ||
              institution.eligibility.reason}
          </p>
        )}
      </div>
      <button
        className="btn btn-sm btn-link"
        onClick={() => select(institution.id)}
      >
        Review
      </button>
    </article>
  );
  return (
    <div className="container py-4 institution-portfolio">
      <div className="portfolio-heading">
        <div>
          <h1>Institution portfolio</h1>
          <p>Manage and activate university partnerships</p>
        </div>
        {data?.capabilities.canRequest && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              setAdding(true);
              setState("AVAILABLE");
              setPage(1);
              setSearchInput("");
            }}
          >
            <Plus size={16} className="me-1" />
            Add partnership
          </button>
        )}
      </div>
      {data && <Summary data={data.summary} />}
      {notice && (
        <p role="status" className="portfolio-notice">
          {notice}
        </p>
      )}
      {adding && (
        <div className="portfolio-notice d-flex justify-content-between align-items-center gap-3">
          <span>
            Review an available institution, its brief and agreement before
            requesting a partnership.
          </span>
          <button
            className="btn btn-sm btn-link"
            onClick={() => {
              setAdding(false);
              setState("");
              setPage(1);
            }}
          >
            Show all
          </button>
        </div>
      )}
      <div className="portfolio-filter">
        <label>
          Institution name
          <input
            type="search"
            className="form-control form-control-sm mt-1"
            placeholder="Search institution…"
            maxLength={200}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </label>
        <label>
          Portfolio status
          <select
            className="form-select form-select-sm mt-1"
            value={state}
            onChange={(e) => {
              setState(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All statuses</option>
            <option value="AVAILABLE">Available</option>
            <option value="IN_PROGRESS">Awaiting approval</option>
            <option value="ACTIVATED">Activated</option>
            <option value="BLOCKED">Action required</option>
          </select>
        </label>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => {
            setSearchInput("");
            setSearch("");
            setState("");
            setPage(1);
            setAdding(false);
          }}
        >
          Clear
        </button>
      </div>
      {error && (
        <div role="alert" className="alert alert-danger">
          {error}
          <button
            className="btn btn-sm btn-outline-danger ms-2"
            onClick={() => setRefresh((n) => n + 1)}
          >
            Retry
          </button>
        </div>
      )}
      <div className="portfolio-register" aria-busy={loading}>
        <div className="portfolio-register__head">
          <span>Institution</span>
          <span>Partnership</span>
          <span>Activation</span>
          <span />
        </div>
        {loading ? (
          <p role="status" className="portfolio-empty">
            Loading institutions…
          </p>
        ) : data?.institutions.length ? (
          data.institutions.map(row)
        ) : (
          !error && (
            <div className="portfolio-empty">
              {search || state
                ? "No matching institutions. Clear the filters to see more."
                : "No institutions are currently available for activation."}
            </div>
          )
        )}
      </div>
      {data && (
        <>
          <p className="small text-muted mt-3">
            {data.pagination.total}{" "}
            {data.pagination.total === 1 ? "institution" : "institutions"}
          </p>
          <DocumentPagination
            page={page}
            pages={data.pagination.pages}
            label="Institution portfolio pages"
            alwaysShow
            disabled={loading}
            change={setPage}
          />
        </>
      )}
      <p className="portfolio-notice">
        Partnership approval and a confirmed, applicable institution agreement
        are both required before submitting applications. Agreements are also
        kept in <Link to="/profile/documents">Agency Documents</Link>.
      </p>
      {selected > 0 && (
        <PortfolioOverlay title="Institution details" close={close} busy={busy}>
          {detailLoading && !detail ? (
            <p role="status">Loading institution…</p>
          ) : detailError && !detail ? (
            <div role="alert">
              {detailError}
              <button
                className="btn btn-sm btn-outline-primary ms-2"
                onClick={() => setRefresh((n) => n + 1)}
              >
                Retry
              </button>
            </div>
          ) : (
            detail && (
              <>
                <section className="portfolio-section">
                  <h2>{detail.institution.name}</h2>
                  <p className="text-muted mt-1">
                    {[
                      detail.institution.city,
                      detail.institution.country ||
                        detail.institution.countries?.name,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                  <span className="portfolio-pill">
                    {detail.partnership
                      ? label(detail.partnership.status)
                      : "Not requested"}
                  </span>
                  <span
                    className={`portfolio-pill ms-2 ${!detail.eligibility.actionRequired && detail.eligibility.canSubmit ? "portfolio-pill--active" : "portfolio-pill--pending"}`}
                  >
                    {detail.eligibility.actionRequired
                      ? "Action required"
                      : detail.eligibility.canSubmit
                        ? "Activated"
                        : "Not activated"}
                  </span>
                  <p className="portfolio-reason">
                    {detail.eligibility.actionReason ||
                      detail.eligibility.reason}
                  </p>
                </section>
                <section className="portfolio-section">
                  <h3>Institution brief</h3>
                  <p style={{ whiteSpace: "pre-wrap" }}>
                    {detail.institution.description?.trim() ||
                      "No institution brief has been provided."}
                  </p>
                </section>
                <section className="portfolio-section">
                  <h3>Applicable agreement</h3>
                  {detail.applicableAgreement ? (
                    <>
                      <dl className="portfolio-metadata">
                        <div>
                          <dt>Revision</dt>
                          <dd>{detail.applicableAgreement.revision}</dd>
                        </div>
                        <div>
                          <dt>Effective</dt>
                          <dd>
                            {date(detail.applicableAgreement.effectiveDate)}
                          </dd>
                        </div>
                        <div>
                          <dt>Expires</dt>
                          <dd>
                            {detail.applicableAgreement.expiresOn
                              ? date(detail.applicableAgreement.expiresOn)
                              : "Does not expire"}
                          </dd>
                        </div>
                        {detail.applicableAgreement.resignDeadline && (
                          <div>
                            <dt>Re-sign by</dt>
                            <dd>
                              {date(detail.applicableAgreement.resignDeadline)}
                            </dd>
                          </div>
                        )}
                      </dl>
                      <div className="portfolio-file">
                        {(detail.applicableAgreement.awaitingSignature ||
                          detail.applicableAgreement.awaitingReview) && (
                          <p className="small text-warning">
                            {detail.applicableAgreement.awaitingSignature
                              ? "Awaiting signature"
                              : "Awaiting administrator review"}
                          </p>
                        )}
                        {detail.applicableAgreement.acceptedSigned && (
                          <span className="portfolio-pill portfolio-pill--active me-2">
                            Accepted signed copy
                          </span>
                        )}
                        <strong>{detail.applicableAgreement.fileName}</strong>
                        <div className="portfolio-file__actions">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            title="Preview agreement"
                            aria-label="Preview agreement"
                            disabled={busy}
                            onClick={() => void file(false)}
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            title="Download agreement"
                            aria-label="Download agreement"
                            disabled={busy}
                            onClick={() => void file(true)}
                          >
                            <Download size={16} />
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p>
                      No applicable agreement has been published. New
                      submissions remain blocked.
                    </p>
                  )}
                </section>
                {detailError && (
                  <p role="alert" className="alert alert-danger">
                    {detailError}
                  </p>
                )}
                {!detail.partnership && data?.capabilities.canRequest && (
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={busy || !detail.applicableAgreement}
                    onClick={() => void request()}
                  >
                    {busy ? "Requesting…" : "Request partnership"}
                  </button>
                )}
                {!detail.capabilities.canSign && (
                  <p className="portfolio-notice">
                    Only the main agency account can return signed institution
                    agreements. Eligible sub-accounts inherit activation after
                    approval and confirmation.
                  </p>
                )}
                {detail.partnership && (
                  <section className="portfolio-section">
                    <h3>Linked Agency Documents</h3>
                    <p className="small text-muted">
                      Download the issued agreement, sign externally and return
                      the signed copy below. An authorised administrator
                      confirms the evidence. These are the same records and
                      files shown in Agency Documents.
                    </p>
                    {detail.documents ? (
                      <AgencyDocumentsWorkspace
                        api={agentAgencyDocumentsApi}
                        accountKey={accountKey}
                        onChanged={() => setRefresh((n) => n + 1)}
                        partnershipId={detail.partnership.id}
                      />
                    ) : (
                      <p className="portfolio-notice">
                        Document access requires explicit agency permission.
                        Contact the main agency account.
                      </p>
                    )}
                  </section>
                )}
              </>
            )
          )}
        </PortfolioOverlay>
      )}
      {preview && <PortfolioFilePreview {...preview} close={closePreview} />}
    </div>
  );
}
